import { CircularProgress, Grid, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import BaseModal from 'components/common/modals/BaseModal'
import DazzedParagraph16 from 'components/common/text/DazzedParagraph16'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'
import { trackEvent } from 'utils/analyticsUtils'
import { isUserOfAnyAdminRole } from 'utils/authUtils'
import { findAssignedTag } from 'utils/tagUtils'
import { selectPickableTags } from '../../../selectors/profileSelector'
import PrimaryButton from '../buttons/PrimaryButton'
import AddTagIcon from '../icons/AddTag'
import GrinMenu from '../menu/GrinMenu'
import TagsPickerInput from './TagsPickerInput'
import TagsPickerList from './TagsPickerList'

const useStyles = makeStyles(theme => ({
  menu: {
    width: 'fit-content',
    minWidth: 300
  },
  actionBtnContainer: {
    padding: 8
  },
  actionBtn: {
    padding: 8,
    height: 'auto'
  },
  progressContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'var(--bg-color-28)',
    zIndex: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}))

const TagsPicker = ({ showIcon = true, onTagPending }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const tags = useSelector(selectPickableTags)
  const doctorId = useSelector(state => state.profileReducer.doctor?.id)
  const patientId = useSelector(state => state.patientsReducer.patient?.id)
  const patientDoctorId = useSelector(state => state.patientsReducer.patient?.doctor?.id)
  const patientTags = useSelector(state => state.patientsReducer.patient?.patientTags?.items)
  const isTagsLoading = useSelector(state => state.patientsReducer.isTagsLoading)
  const nextToken = useSelector(state => state.profileReducer.doctor?.tags?.nextToken)
  const isLoadingMore = useSelector(state => state.profileReducer.doctor?.tags?.isLoadingMore)
  const isSearchingTags = useSelector(state => state.profileReducer.doctor?.tags?.isSearchingTags)

  const { count: numOfpatientsWithTag, isLoading: isLoadingPatientsWithTag } = useSelector(
    state => state.patientsReducer.patientsByTag
  )

  const [input, setInput] = useState('')
  const [debouncedInput] = useDebounce(input, 300)
  const [isHiddenTagsDropdownOpen, setIsHiddenTagsDropdownOpen] = useState(false)
  const [tagToDelete, setTagToDelete] = useState(null)
  const [pendingTagValues, setPendingTagValues] = useState(new Set())
  const [isAddingTag, setIsAddingTag] = useState(false)
  const [showTagAdded, setShowTagAdded] = useState(false)

  const isSubmitting = useRef(false)
  const skipNextSearch = useRef(false)
  const tagAddedTimerRef = useRef(null)

  const normalizedInputRaw = useMemo(() => input?.trim().toLowerCase(), [input])

  const isTagAssigned = useCallback(tagValue => findAssignedTag(patientTags, tagValue), [patientTags])

  const isInputValid = useMemo(
    () => !!input && !!normalizedInputRaw && !isTagAssigned(normalizedInputRaw),
    [isTagAssigned, input, normalizedInputRaw]
  )

  useEffect(() => {
    if (!isTagsLoading) {
      isSubmitting.current = false
      if (isAddingTag) {
        setIsAddingTag(false)
        setShowTagAdded(true)
        tagAddedTimerRef.current = setTimeout(() => {
          setShowTagAdded(false)
          setInput('')
        }, 2000)
      }
    }
  }, [isTagsLoading, isAddingTag])

  useEffect(() => {
    return () => {
      if (tagAddedTimerRef.current) clearTimeout(tagAddedTimerRef.current)
    }
  }, [])

  // Once the backend confirms a tag assignment (patientTags updates), remove it from the optimistic pending list
  useEffect(() => {
    setPendingTagValues(prev => {
      if (!prev.size) return prev
      const assigned = new Set(patientTags?.map(t => t.tag?.value) ?? [])
      const next = new Set([...prev].filter(v => !assigned.has(v)))
      return next.size === prev.size ? prev : next
    })
  }, [patientTags])

  // Dispatch server-side search only when the user types (debouncedInput changes).
  // isHiddenTagsDropdownOpen is intentionally NOT in the deps — it is read as a gate
  // so the search doesn't fire when the picker is closed, but adding it as a dep would
  // trigger fetchDoctorTags() on every open, replacing doctor.tags.items and losing any
  // tags that were added in-memory (e.g. via addTagIfAbsent after creation).
  // The initial tag load on first open is handled by handlePickerOpen independently.
  useEffect(() => {
    if (!isHiddenTagsDropdownOpen) return
    if (skipNextSearch.current) {
      skipNextSearch.current = false
      return
    }
    dispatch(Actions.searchDoctorTags(debouncedInput.trim()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput, dispatch])

  const handleLoadMore = useCallback(() => {
    if (nextToken && !isLoadingMore && !isSearchingTags) {
      dispatch(Actions.fetchMoreDoctorTags())
    }
  }, [nextToken, isLoadingMore, isSearchingTags, dispatch])

  const handleAddTag = useCallback(() => {
    if (isInputValid && !isSubmitting.current) {
      isSubmitting.current = true
      trackEvent('Tags picker - Custom tag added', {
        tag: input.trim()
      })
      dispatch(Actions.assignPatientTag({ tag: input.trim(), patientId }))
      skipNextSearch.current = true
      setIsAddingTag(true)
    }
  }, [isInputValid, patientId, input, dispatch])

  const displayedTags = useMemo(
    () => tags?.filter(tag => !pendingTagValues.has(tag.value)) ?? [],
    [tags, pendingTagValues]
  )

  const handleSelectTag = useCallback(
    tag => {
      if (isTagAssigned(tag.value) || pendingTagValues.has(tag.value)) {
        return
      }
      setPendingTagValues(prev => new Set([...prev, tag.value]))
      onTagPending?.(tag)
      trackEvent('Tags picker - tag selected', {
        tag: tag.value
      })
      dispatch(Actions.assignPatientTag({ tag: tag.value, patientId }))
      document.activeElement?.blur()
    },
    [dispatch, patientId, isTagAssigned, pendingTagValues, onTagPending]
  )

  const onDeleteTag = useCallback(
    tag => {
      trackEvent('Tags picker - delete tag clicked', {
        tagId: tag.id,
        tagValue: tag.value,
        doctorId
      })
      setTagToDelete(tag)
      dispatch(Actions.fetchPatientsWithTag(tag))
    },
    [dispatch, doctorId]
  )

  const handleCloseDeleteTagConfirmation = useCallback(() => {
    trackEvent('Tags picker - tag deletion canceled', {
      tagId: tagToDelete.id,
      tagValue: tagToDelete.value,
      doctorId
    })
    setTagToDelete(null)
  }, [doctorId, tagToDelete])

  const handleDeleteTag = useCallback(() => {
    trackEvent('Tags picker - tag deletion confirmed', {
      tagId: tagToDelete.id,
      tagValue: tagToDelete.value,
      doctorId
    })
    dispatch(Actions.deleteDoctorTag(tagToDelete))
    setTagToDelete(null)
  }, [dispatch, doctorId, tagToDelete])

  const handlePickerOpen = useCallback(() => {
    const isAdmin = isUserOfAnyAdminRole()

    if (!tags?.length) {
      if (isAdmin) {
        dispatch(Actions.mpFetchCustomTagsByDoctor({ doctorId: patientDoctorId }))
      } else {
        dispatch(Actions.fetchDoctorTags())
      }
    }
    setIsHiddenTagsDropdownOpen(true)
  }, [dispatch, patientDoctorId, tags])

  return (
    <div>
      <GrinMenu
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        className={classes.menu}
        open={isHiddenTagsDropdownOpen}
        onOpen={handlePickerOpen}
        onClose={() => {
          setIsHiddenTagsDropdownOpen(false)
          setInput('')
          setShowTagAdded(false)
          if (tagAddedTimerRef.current) clearTimeout(tagAddedTimerRef.current)
          dispatch(Actions.searchDoctorTags(''))
        }}
        triggerComponent={
          showIcon ? (
            <IconButton size="small" id="tagsList-add-tag">
              <AddTagIcon />
            </IconButton>
          ) : null
        }
      >
        <TagsPickerInput
          value={input}
          setValue={setInput}
          onEnterPressed={handleAddTag}
          disabled={isTagsLoading || showTagAdded}
          isLoading={isAddingTag}
          showSuccess={showTagAdded}
        />
        <TagsPickerList
          tags={displayedTags}
          onTagSelected={handleSelectTag}
          onDeleteTag={onDeleteTag}
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore || isSearchingTags}
          hasMore={!!nextToken}
        />
        <div className={classes.actionBtnContainer}>
          <PrimaryButton
            className={classes.actionBtn}
            width="100%"
            label={t('pages.patients.selectedPatient.tags.addTag')}
            disabled={!isInputValid || isTagsLoading}
            onClick={handleAddTag}
          />
        </div>
      </GrinMenu>
      <BaseModal
        title={t('pages.patients.selectedPatient.tags.deleteTagTitle')}
        variant="alert"
        open={!!tagToDelete}
        handleClose={handleCloseDeleteTagConfirmation}
        secondaryLabel={t('general.cancel')}
        primaryLabel={t('general.delete')}
        onSecondaryBtnClick={handleCloseDeleteTagConfirmation}
        onPrimaryBtnClick={handleDeleteTag}
        reverseActionsOrder
        largerButtons
        primaryButtonId="deleteTag-confirm"
        secondaryButtonId="deleteTag-cancel"
      >
        <Grid container justifyContent="center" alignItems="center" style={{ padding: 10 }}>
          <Grid item>
            {isLoadingPatientsWithTag ? (
              <CircularProgress />
            ) : (
              <DazzedParagraph16 textAlign="center">
                {numOfpatientsWithTag === 0 ? (
                  t('pages.patients.selectedPatient.tags.deleteTagFromZeroPatients', { tag: tagToDelete?.value })
                ) : (
                  <Trans
                    values={{
                      tag: tagToDelete?.value,
                      count: numOfpatientsWithTag
                    }}
                    i18nKey={`pages.patients.selectedPatient.tags.deleteTagConfirmation${
                      numOfpatientsWithTag === 1 ? 'Single' : 'Multiple'
                    }`}
                  />
                )}
              </DazzedParagraph16>
            )}
          </Grid>
        </Grid>
      </BaseModal>
    </div>
  )
}

export default TagsPicker
